import * as THREE from 'three';
import {
    isSketchArcObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    type SketchArcObject,
    type SketchEllipseObject,
    type SketchLineObject,
    type SketchParabolaObject,
    type SketchSplineObject,
    type SketchStyleSplineObject,
} from '../../mesh/sketch';
import {SketchTrimGeometryService} from './SketchTrimGeometryService';
import {
    SketchTrimCurveKind,
    SketchTrimEndpointRole,
    type SketchTrimCurveData,
    type SketchTrimCurveObject,
} from './SketchTrimTypes';

/**
 * 开放曲线 Corner 几何服务。
 *
 * 职责：
 * 1. 判断草图实体是否具备 Corner 所需的明确起终端点；
 * 2. 在曲线自然支撑几何上寻找交点，而不是把端点按切线临时拼接；
 * 3. 根据两侧点击位置筛选能保留点击段的唯一角点候选；
 * 4. 只返回几何决策，不修改实体、拓扑、尺寸或历史状态。
 */
export class SketchTrimCornerGeometryService {
    /** 完整周期。 */
    private static readonly FULL_SWEEP = Math.PI * 2;

    /** 参数比较容差。 */
    private static readonly PARAMETER_TOLERANCE = 1e-7;

    /** 隐式方程根容差。 */
    private static readonly ROOT_TOLERANCE = 1e-7;

    /** 交点空间容差。 */
    private static readonly POINT_TOLERANCE = 1e-5;

    /** 有界曲线求根采样数。 */
    private static readonly ROOT_SAMPLE_COUNT = 512;

    /** 公共 Trim 几何能力。 */
    private readonly geometryService: SketchTrimGeometryService;

    /**
     * 创建开放曲线 Corner 几何服务。
     *
     * @param geometryService 公共 Trim 几何服务。
     */
    public constructor(geometryService: SketchTrimGeometryService) {
        this.geometryService = geometryService;
    }

    /**
     * 判断对象是否为 Corner 支持的开放草图曲线。
     *
     * 完整圆、完整椭圆和闭合样条没有可被点击侧唯一解释的起终端点，
     * 因此不会进入 Corner 选择集合。
     *
     * @param value 待判断对象。
     * @returns 支持时返回 true。
     */
    public isSupportedOpenCurveObject(value: unknown): value is SketchTrimCurveObject {
        if (isSketchLineObject(value) || isSketchParabolaObject(value)) {
            return true;
        }
        if (isSketchArcObject(value)) {
            const definition = value.getArcDefinition();
            return this.resolveAngleSweep(
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
            ) < SketchTrimCornerGeometryService.FULL_SWEEP - SketchTrimCornerGeometryService.PARAMETER_TOLERANCE;
        }
        if (isSketchEllipseObject(value)) {
            const definition = value.getEllipseDefinition();
            return this.resolveAngleSweep(
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
            ) < SketchTrimCornerGeometryService.FULL_SWEEP - SketchTrimCornerGeometryService.PARAMETER_TOLERANCE;
        }
        if (isSketchSplineObject(value) || isSketchStyleSplineObject(value)) {
            return !value.isClosedGeometry;
        }
        return false;
    }

    /**
     * 解析两条开放曲线的 Corner 几何结果。
     *
     * @param firstCurveData 第一条曲线数据。
     * @param firstHitPoint 第一条曲线点击位置。
     * @param secondCurveData 第二条曲线数据。
     * @param secondHitPoint 第二条曲线点击位置。
     * @returns 距离两次点击位置最近的有效结果；无法沿自然路径形成角点时返回 null。
     */
    public resolveCorner(
        firstCurveData: SketchTrimCurveData,
        firstHitPoint: THREE.Vector3,
        secondCurveData: SketchTrimCurveData,
        secondHitPoint: THREE.Vector3,
    ): {
        point: THREE.Vector3;
        firstParameterT: number;
        secondParameterT: number;
        firstEndpointRole: SketchTrimEndpointRole;
        secondEndpointRole: SketchTrimEndpointRole;
    } | null {
        if (
            firstCurveData.curve === secondCurveData.curve
            || firstCurveData.isClosed
            || secondCurveData.isClosed
        ) {
            return null;
        }
        if (
            !this.hasValidSupportGeometry(firstCurveData)
            || !this.hasValidSupportGeometry(secondCurveData)
        ) {
            return null;
        }

        const firstPickT = this.geometryService.projectPointToCurveParameter(firstCurveData, firstHitPoint);
        const secondPickT = this.geometryService.projectPointToCurveParameter(secondCurveData, secondHitPoint);
        const candidates = this.collectSupportIntersectionCandidates(firstCurveData, secondCurveData);

        // Corner 必须同时保留两次点击所在的曲线区段，禁止选择会裁掉点击侧的另一交点。
        const resolvedCandidates = candidates
            .map((candidate) => {
                const firstEndpointRole = this.geometryService.resolveEndpointRoleByIntersectionAndPick(
                    candidate.firstParameterT,
                    firstPickT,
                );
                const secondEndpointRole = this.geometryService.resolveEndpointRoleByIntersectionAndPick(
                    candidate.secondParameterT,
                    secondPickT,
                );
                if (
                    !this.isCandidateCompatibleWithPick(candidate.firstParameterT, firstPickT, firstEndpointRole)
                    || !this.isCandidateCompatibleWithPick(candidate.secondParameterT, secondPickT, secondEndpointRole)
                ) {
                    return null;
                }

                return {
                    ...candidate,
                    firstEndpointRole,
                    secondEndpointRole,
                    // 不同曲线的归一化参数没有统一长度尺度，不能直接用于空间交点优先级。
                    spatialDistanceScore: candidate.point.distanceTo(firstHitPoint)
                        + candidate.point.distanceTo(secondHitPoint),
                    // 同一空间交点可能对应多组周期参数，仅用参数行程选择最短自然路径表示。
                    parameterTravelScore: Math.abs(candidate.firstParameterT - firstPickT)
                        + Math.abs(candidate.secondParameterT - secondPickT),
                };
            })
            .filter((candidate): candidate is NonNullable<typeof candidate> => candidate !== null)
            .sort((left, right) => {
                const spatialDistanceDelta = left.spatialDistanceScore - right.spatialDistanceScore;
                if (Math.abs(spatialDistanceDelta) > SketchTrimCornerGeometryService.POINT_TOLERANCE) {
                    return spatialDistanceDelta;
                }
                const parameterTravelDelta = left.parameterTravelScore - right.parameterTravelScore;
                if (Math.abs(parameterTravelDelta) > SketchTrimCornerGeometryService.PARAMETER_TOLERANCE) {
                    return parameterTravelDelta;
                }
                // 完全对称时按空间坐标稳定排序，保证交换选择顺序不会改变结果。
                return left.point.x - right.point.x
                    || left.point.y - right.point.y
                    || left.point.z - right.point.z;
            });

        const selected = resolvedCandidates[0];
        if (!selected) {
            return null;
        }
        return {
            point: selected.point,
            firstParameterT: selected.firstParameterT,
            secondParameterT: selected.secondParameterT,
            firstEndpointRole: selected.firstEndpointRole,
            secondEndpointRole: selected.secondEndpointRole,
        };
    }

    /**
     * 收集两条自然支撑曲线的交点候选。
     *
     * @param firstCurveData 第一条曲线。
     * @param secondCurveData 第二条曲线。
     * @returns 去重后的交点与双方参数。
     */
    private collectSupportIntersectionCandidates(
        firstCurveData: SketchTrimCurveData,
        secondCurveData: SketchTrimCurveData,
    ): Array<{point: THREE.Vector3; firstParameterT: number; secondParameterT: number}> {
        const candidates: Array<{point: THREE.Vector3; firstParameterT: number; secondParameterT: number}> = [];

        // 两条直线沿无限支撑线精确求交，完整保留既有 Corner 数学语义。
        if (
            firstCurveData.curveKind === SketchTrimCurveKind.Line
            && secondCurveData.curveKind === SketchTrimCurveKind.Line
        ) {
            const firstLineData = this.geometryService.resolveLineData(
                firstCurveData.curve as SketchLineObject,
                firstCurveData.segmentIndex,
            );
            const secondLineData = this.geometryService.resolveLineData(
                secondCurveData.curve as SketchLineObject,
                secondCurveData.segmentIndex,
            );
            const intersection = this.geometryService.resolveLineIntersectionParameter(
                firstLineData.start,
                firstLineData.end,
                secondLineData.start,
                secondLineData.end,
                firstLineData.normal,
            );
            return intersection ? [{
                point: intersection.point,
                firstParameterT: intersection.targetT,
                secondParameterT: intersection.sourceT,
            }] : [];
        }

        // 先收集双方当前可见参数域中的交点，尤其覆盖样条与样条的既有精确裁剪能力。
        this.geometryService.collectCurveBoundaryParameters(firstCurveData, [secondCurveData], false)
            .forEach((firstParameterT) => {
                const point = this.evaluateCurve(firstCurveData, firstParameterT);
                if (!point) {
                    return;
                }
                this.projectPointToSupportParameters(secondCurveData, point).forEach((secondParameterT) => {
                    this.pushCandidate(candidates, firstCurveData, secondCurveData, point, firstParameterT, secondParameterT);
                });
            });

        // 样条没有稳定的端外自然延伸定义，但另一条解析曲线仍可延伸到样条当前参数域。
        if (this.isSplineFamily(firstCurveData)) {
            if (!this.isSplineFamily(secondCurveData)) {
                this.collectFiniteParametricImplicitCandidates(
                    firstCurveData,
                    secondCurveData,
                    false,
                    candidates,
                );
            }
            return candidates;
        }
        if (this.isSplineFamily(secondCurveData)) {
            this.collectFiniteParametricImplicitCandidates(
                secondCurveData,
                firstCurveData,
                true,
                candidates,
            );
            return candidates;
        }

        // 圆弧和部分椭圆的自然支撑曲线有有限周期，优先在完整可延伸域上求根。
        if (this.hasFiniteNaturalDomain(firstCurveData)) {
            this.collectFiniteParametricImplicitCandidates(
                firstCurveData,
                secondCurveData,
                false,
                candidates,
            );
            return candidates;
        }
        if (this.hasFiniteNaturalDomain(secondCurveData)) {
            this.collectFiniteParametricImplicitCandidates(
                secondCurveData,
                firstCurveData,
                true,
                candidates,
            );
            return candidates;
        }

        // 剩余组合只可能是直线与抛物线或两条抛物线，其隐式复合函数最高为四次多项式。
        const parameterCurveData = firstCurveData.curveKind === SketchTrimCurveKind.Parabola
            ? firstCurveData
            : secondCurveData;
        const implicitCurveData = parameterCurveData === firstCurveData ? secondCurveData : firstCurveData;
        this.collectUnboundedPolynomialCandidates(
            parameterCurveData,
            implicitCurveData,
            parameterCurveData !== firstCurveData,
            candidates,
        );
        return candidates;
    }

    /**
     * 在有界参数曲线上求解另一条隐式支撑曲线。
     *
     * @param parameterCurveData 参数曲线。
     * @param implicitCurveData 隐式曲线。
     * @param shouldSwapResult 是否交换输出双方参数。
     * @param candidates 结果集合。
     */
    private collectFiniteParametricImplicitCandidates(
        parameterCurveData: SketchTrimCurveData,
        implicitCurveData: SketchTrimCurveData,
        shouldSwapResult: boolean,
        candidates: Array<{point: THREE.Vector3; firstParameterT: number; secondParameterT: number}>,
    ): void {
        const implicitEvaluator = this.createImplicitEvaluator(implicitCurveData);
        if (!implicitEvaluator) {
            return;
        }
        const domain = this.resolveNaturalParameterDomain(parameterCurveData);
        const roots = this.collectFiniteScalarRoots(
            (parameterT) => {
                const point = this.evaluateCurve(parameterCurveData, parameterT);
                return point ? implicitEvaluator(point) : Number.NaN;
            },
            domain.startT,
            domain.endT,
        );

        roots.forEach((parameterT) => {
            const point = this.evaluateCurve(parameterCurveData, parameterT);
            if (!point) {
                return;
            }
            this.projectPointToSupportParameters(implicitCurveData, point).forEach((implicitParameterT) => {
                const firstParameterT = shouldSwapResult ? implicitParameterT : parameterT;
                const secondParameterT = shouldSwapResult ? parameterT : implicitParameterT;
                this.pushCandidate(
                    candidates,
                    shouldSwapResult ? implicitCurveData : parameterCurveData,
                    shouldSwapResult ? parameterCurveData : implicitCurveData,
                    point,
                    firstParameterT,
                    secondParameterT,
                );
            });
        });
    }

    /**
     * 求解无界抛物线参数与另一隐式曲线组成的低阶多项式。
     *
     * @param parameterCurveData 无界参数曲线。
     * @param implicitCurveData 隐式曲线。
     * @param shouldSwapResult 是否交换输出双方参数。
     * @param candidates 结果集合。
     */
    private collectUnboundedPolynomialCandidates(
        parameterCurveData: SketchTrimCurveData,
        implicitCurveData: SketchTrimCurveData,
        shouldSwapResult: boolean,
        candidates: Array<{point: THREE.Vector3; firstParameterT: number; secondParameterT: number}>,
    ): void {
        const implicitEvaluator = this.createImplicitEvaluator(implicitCurveData);
        if (!implicitEvaluator) {
            return;
        }

        // 抛物线代入直线隐式式为二次，代入抛物线隐式式最高为四次。
        const degree = implicitCurveData.curveKind === SketchTrimCurveKind.Line ? 2 : 4;
        const values = Array.from({length: degree + 1}, (_, index) => {
            const point = this.evaluateCurve(parameterCurveData, index);
            return point ? implicitEvaluator(point) : Number.NaN;
        });
        if (values.some((value) => !Number.isFinite(value))) {
            return;
        }
        const coefficients = this.interpolatePolynomial(values);
        this.solveRealPolynomialRoots(coefficients).forEach((parameterT) => {
            const point = this.evaluateCurve(parameterCurveData, parameterT);
            if (!point) {
                return;
            }
            this.projectPointToSupportParameters(implicitCurveData, point).forEach((implicitParameterT) => {
                const firstParameterT = shouldSwapResult ? implicitParameterT : parameterT;
                const secondParameterT = shouldSwapResult ? parameterT : implicitParameterT;
                this.pushCandidate(
                    candidates,
                    shouldSwapResult ? implicitCurveData : parameterCurveData,
                    shouldSwapResult ? parameterCurveData : implicitCurveData,
                    point,
                    firstParameterT,
                    secondParameterT,
                );
            });
        });
    }

    /**
     * 创建曲线支撑几何的隐式方程。
     *
     * @param curveData 曲线数据。
     * @returns 隐式值函数；样条没有稳定隐式定义时返回 null。
     */
    private createImplicitEvaluator(curveData: SketchTrimCurveData): ((point: THREE.Vector3) => number) | null {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const line = curveData.curve as SketchLineObject;
            const lineData = this.geometryService.resolveLineData(
                line,
                curveData.segmentIndex,
            );
            const localStart = line.referencePlane.worldToLocal(lineData.start);
            const localEnd = line.referencePlane.worldToLocal(lineData.end);
            const direction = localEnd.clone().sub(localStart);
            const length = direction.length();
            if (length <= SketchTrimCornerGeometryService.POINT_TOLERANCE) {
                return null;
            }
            return (point) => {
                const localPoint = line.referencePlane.worldToLocal(point);
                return direction.x * (localPoint.y - localStart.y) / length
                    - direction.y * (localPoint.x - localStart.x) / length;
            };
        }
        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            const definition = (curveData.curve as SketchArcObject).getArcDefinition();
            const radiusSquared = definition.radius * definition.radius;
            return (point) => point.distanceToSquared(definition.centerWorld) / radiusSquared - 1;
        }
        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            const ellipse = curveData.curve as SketchEllipseObject;
            const definition = ellipse.getEllipseDefinition();
            return (point) => {
                const canonicalPoint = this.resolveEllipseCanonicalPoint(ellipse, point);
                const normalizedX = canonicalPoint.x / definition.radiusX;
                const normalizedY = canonicalPoint.y / definition.radiusY;
                return normalizedX * normalizedX + normalizedY * normalizedY - 1;
            };
        }
        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            const frame = this.resolveParabolaFrame(curveData.curve as SketchParabolaObject);
            if (!frame) {
                return null;
            }
            return (point) => {
                const offset = point.clone().sub(frame.apexWorld);
                const side = offset.dot(frame.sideDirection);
                const axis = offset.dot(frame.axisDirection);
                return axis - side * side / (4 * frame.focalLength);
            };
        }
        return null;
    }

    /**
     * 计算指定参数处的支撑曲线世界坐标。
     *
     * @param curveData 曲线数据。
     * @param parameterT 允许超出 `[0,1]` 的自然参数。
     * @returns 世界坐标；曲线定义无效时返回 null。
     */
    private evaluateCurve(curveData: SketchTrimCurveData, parameterT: number): THREE.Vector3 | null {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const lineData = this.geometryService.resolveLineData(
                curveData.curve as SketchLineObject,
                curveData.segmentIndex,
            );
            return lineData.start.clone().addScaledVector(lineData.end.clone().sub(lineData.start), parameterT);
        }
        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            const arc = curveData.curve as SketchArcObject;
            const definition = arc.getArcDefinition();
            const sweep = this.resolveAngleSweep(definition.startAngle, definition.endAngle, definition.clockwise);
            const angle = definition.clockwise
                ? definition.startAngle - sweep * parameterT
                : definition.startAngle + sweep * parameterT;
            return this.resolvePlanarConicPoint(
                arc.referencePlane,
                definition.centerWorld,
                definition.radius,
                definition.radius,
                0,
                angle,
            );
        }
        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            const ellipse = curveData.curve as SketchEllipseObject;
            const definition = ellipse.getEllipseDefinition();
            const sweep = this.resolveAngleSweep(definition.startAngle, definition.endAngle, definition.clockwise);
            const angle = definition.clockwise
                ? definition.startAngle - sweep * parameterT
                : definition.startAngle + sweep * parameterT;
            return this.resolvePlanarConicPoint(
                ellipse.referencePlane,
                definition.centerWorld,
                definition.radiusX,
                definition.radiusY,
                definition.rotation,
                angle,
            );
        }
        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            const parabola = curveData.curve as SketchParabolaObject;
            const definition = parabola.getParabolaDefinition();
            const frame = this.resolveParabolaFrame(parabola);
            if (!frame) {
                return null;
            }
            const parameter = THREE.MathUtils.lerp(
                definition.startParameter,
                definition.endParameter,
                parameterT,
            );
            return frame.apexWorld.clone()
                .addScaledVector(frame.sideDirection, parameter)
                .addScaledVector(frame.axisDirection, parameter * parameter / (4 * frame.focalLength));
        }

        // 样条 Corner 只使用当前开放参数域；按正式显示曲线点插值，禁止在端外伪造切线延伸。
        const curvePoints = (
            curveData.curve as SketchSplineObject | SketchStyleSplineObject
        ).getCurvePoints();
        if (curvePoints.length < 2) {
            return new THREE.Vector3();
        }
        const scaledIndex = THREE.MathUtils.clamp(parameterT, 0, 1) * (curvePoints.length - 1);
        const startIndex = Math.min(Math.floor(scaledIndex), curvePoints.length - 2);
        return curvePoints[startIndex].clone().lerp(curvePoints[startIndex + 1], scaledIndex - startIndex);
    }

    /**
     * 将交点投影回曲线自然支撑参数。
     *
     * @param curveData 曲线数据。
     * @param point 世界交点。
     * @returns 所有合法自然参数。
     */
    private projectPointToSupportParameters(curveData: SketchTrimCurveData, point: THREE.Vector3): number[] {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const lineData = this.geometryService.resolveLineData(
                curveData.curve as SketchLineObject,
                curveData.segmentIndex,
            );
            return [this.geometryService.projectPointToSegmentParameter(lineData.start, lineData.end, point)];
        }
        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            const arc = curveData.curve as SketchArcObject;
            const definition = arc.getArcDefinition();
            const localCenter = arc.referencePlane.worldToLocal(definition.centerWorld);
            const localPoint = arc.referencePlane.worldToLocal(point);
            const angle = Math.atan2(localPoint.y - localCenter.y, localPoint.x - localCenter.x);
            return this.collectPeriodicParameters(
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
                angle,
            );
        }
        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            const ellipse = curveData.curve as SketchEllipseObject;
            const definition = ellipse.getEllipseDefinition();
            const canonicalPoint = this.resolveEllipseCanonicalPoint(ellipse, point);
            const normalizedX = canonicalPoint.x / definition.radiusX;
            const normalizedY = canonicalPoint.y / definition.radiusY;
            if (Math.abs(normalizedX * normalizedX + normalizedY * normalizedY - 1) > 1e-4) {
                return [];
            }
            return this.collectPeriodicParameters(
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
                Math.atan2(normalizedY, normalizedX),
            );
        }
        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            const parabola = curveData.curve as SketchParabolaObject;
            const definition = parabola.getParabolaDefinition();
            const frame = this.resolveParabolaFrame(parabola);
            if (!frame) {
                return [];
            }
            const parameter = point.clone().sub(frame.apexWorld).dot(frame.sideDirection);
            const span = definition.endParameter - definition.startParameter;
            if (Math.abs(span) <= SketchTrimCornerGeometryService.PARAMETER_TOLERANCE) {
                return [];
            }
            const parameterT = (parameter - definition.startParameter) / span;
            const projectedPoint = this.evaluateCurve(curveData, parameterT);
            return projectedPoint
                && projectedPoint.distanceTo(point) <= SketchTrimCornerGeometryService.POINT_TOLERANCE
                ? [parameterT]
                : [];
        }

        const parameterT = this.geometryService.projectPointToCurveParameter(curveData, point);
        const projectedPoint = this.evaluateCurve(curveData, parameterT);
        return projectedPoint
            && projectedPoint.distanceTo(point) <= SketchTrimCornerGeometryService.POINT_TOLERANCE
            ? [parameterT]
            : [];
    }

    /**
     * 解析圆弧或部分椭圆同一点对应的所有合法周期参数。
     *
     * @param startAngle 起始角。
     * @param endAngle 终止角。
     * @param clockwise 是否顺时针。
     * @param pointAngle 交点角度。
     * @returns 自然延伸域内的参数列表。
     */
    private collectPeriodicParameters(
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
        pointAngle: number,
    ): number[] {
        const sweep = this.resolveAngleSweep(startAngle, endAngle, clockwise);
        if (sweep <= SketchTrimCornerGeometryService.PARAMETER_TOLERANCE) {
            return [];
        }
        const directedProgress = clockwise
            ? this.normalizePositiveAngle(startAngle - pointAngle)
            : this.normalizePositiveAngle(pointAngle - startAngle);
        const baseParameter = directedProgress / sweep;
        const period = SketchTrimCornerGeometryService.FULL_SWEEP / sweep;
        const domainStart = 1 - period + SketchTrimCornerGeometryService.PARAMETER_TOLERANCE;
        const domainEnd = period - SketchTrimCornerGeometryService.PARAMETER_TOLERANCE;
        const parameters: number[] = [];

        for (let periodIndex = -2; periodIndex <= 2; periodIndex += 1) {
            const parameterT = baseParameter + periodIndex * period;
            if (parameterT < domainStart || parameterT > domainEnd) {
                continue;
            }
            parameters.push(parameterT);
        }
        return this.dedupeNumbers(parameters);
    }

    /**
     * 追加通过双方参数域校验的候选。
     *
     * @param candidates 候选集合。
     * @param firstCurveData 第一条曲线。
     * @param secondCurveData 第二条曲线。
     * @param point 交点。
     * @param firstParameterT 第一参数。
     * @param secondParameterT 第二参数。
     */
    private pushCandidate(
        candidates: Array<{point: THREE.Vector3; firstParameterT: number; secondParameterT: number}>,
        firstCurveData: SketchTrimCurveData,
        secondCurveData: SketchTrimCurveData,
        point: THREE.Vector3,
        firstParameterT: number,
        secondParameterT: number,
    ): void {
        if (
            !this.isParameterInNaturalDomain(firstCurveData, firstParameterT)
            || !this.isParameterInNaturalDomain(secondCurveData, secondParameterT)
        ) {
            return;
        }
        const isDuplicate = candidates.some((candidate) => (
            candidate.point.distanceToSquared(point)
                <= SketchTrimCornerGeometryService.POINT_TOLERANCE ** 2
            && Math.abs(candidate.firstParameterT - firstParameterT)
                <= SketchTrimCornerGeometryService.PARAMETER_TOLERANCE
            && Math.abs(candidate.secondParameterT - secondParameterT)
                <= SketchTrimCornerGeometryService.PARAMETER_TOLERANCE
        ));
        if (!isDuplicate) {
            candidates.push({point: point.clone(), firstParameterT, secondParameterT});
        }
    }

    /**
     * 判断候选是否保留点击侧且不会把实体压缩为零长度。
     *
     * @param intersectionT 交点参数。
     * @param pickT 点击参数。
     * @param endpointRole 待移动端点。
     * @returns 合法时返回 true。
     */
    private isCandidateCompatibleWithPick(
        intersectionT: number,
        pickT: number,
        endpointRole: SketchTrimEndpointRole,
    ): boolean {
        const tolerance = SketchTrimCornerGeometryService.PARAMETER_TOLERANCE;
        if (endpointRole === SketchTrimEndpointRole.Start) {
            return intersectionT < 1 - tolerance && pickT >= intersectionT - tolerance;
        }
        return intersectionT > tolerance && pickT <= intersectionT + tolerance;
    }

    /**
     * 判断曲线是否属于样条族。
     *
     * @param curveData 曲线数据。
     * @returns 样条或样式样条返回 true。
     */
    private isSplineFamily(curveData: SketchTrimCurveData): boolean {
        return curveData.curveKind === SketchTrimCurveKind.Spline
            || curveData.curveKind === SketchTrimCurveKind.StyleSpline;
    }

    /**
     * 判断曲线是否具备有限自然延伸域。
     *
     * @param curveData 曲线数据。
     * @returns 圆弧或部分椭圆返回 true。
     */
    private hasFiniteNaturalDomain(curveData: SketchTrimCurveData): boolean {
        return curveData.curveKind === SketchTrimCurveKind.Arc
            || curveData.curveKind === SketchTrimCurveKind.Ellipse;
    }

    /**
     * 解析曲线的自然参数域。
     *
     * 圆弧和部分椭圆的端外延伸不得达到或越过完整周期，避免开放实体退化为闭合实体；
     * 样条只允许当前参数域，直线和抛物线由多项式链路处理无界域。
     *
     * @param curveData 曲线数据。
     * @returns 参数域。
     */
    private resolveNaturalParameterDomain(curveData: SketchTrimCurveData): {startT: number; endT: number} {
        if (this.isSplineFamily(curveData)) {
            return {startT: 0, endT: 1};
        }
        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            const definition = (curveData.curve as SketchArcObject).getArcDefinition();
            return this.resolvePeriodicNaturalDomain(
                this.resolveAngleSweep(definition.startAngle, definition.endAngle, definition.clockwise),
            );
        }
        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            const definition = (curveData.curve as SketchEllipseObject).getEllipseDefinition();
            return this.resolvePeriodicNaturalDomain(
                this.resolveAngleSweep(definition.startAngle, definition.endAngle, definition.clockwise),
            );
        }
        return {startT: Number.NEGATIVE_INFINITY, endT: Number.POSITIVE_INFINITY};
    }

    /**
     * 按当前扫掠角计算开放周期曲线的最大自然延伸域。
     *
     * @param sweep 当前扫掠角。
     * @returns 不会形成完整周期的参数域。
     */
    private resolvePeriodicNaturalDomain(sweep: number): {startT: number; endT: number} {
        const period = SketchTrimCornerGeometryService.FULL_SWEEP / sweep;
        return {
            startT: 1 - period + SketchTrimCornerGeometryService.PARAMETER_TOLERANCE,
            endT: period - SketchTrimCornerGeometryService.PARAMETER_TOLERANCE,
        };
    }

    /**
     * 判断参数是否落在实体允许的自然延伸域内。
     *
     * @param curveData 曲线数据。
     * @param parameterT 参数。
     * @returns 合法时返回 true。
     */
    private isParameterInNaturalDomain(curveData: SketchTrimCurveData, parameterT: number): boolean {
        if (!Number.isFinite(parameterT)) {
            return false;
        }
        if (curveData.curveKind === SketchTrimCurveKind.Line || curveData.curveKind === SketchTrimCurveKind.Parabola) {
            return true;
        }
        const domain = this.resolveNaturalParameterDomain(curveData);
        return parameterT >= domain.startT && parameterT <= domain.endT;
    }

    /**
     * 收集有限区间内标量函数的全部根。
     *
     * 除异号区间外还检查绝对值局部极小点，因此相切场景不会因没有符号翻转而漏解。
     *
     * @param evaluator 标量函数。
     * @param startT 区间起点。
     * @param endT 区间终点。
     * @returns 去重根列表。
     */
    private collectFiniteScalarRoots(
        evaluator: (parameterT: number) => number,
        startT: number,
        endT: number,
    ): number[] {
        const samples = Array.from(
            {length: SketchTrimCornerGeometryService.ROOT_SAMPLE_COUNT + 1},
            (_, index) => {
                const parameterT = THREE.MathUtils.lerp(
                    startT,
                    endT,
                    index / SketchTrimCornerGeometryService.ROOT_SAMPLE_COUNT,
                );
                return {parameterT, value: evaluator(parameterT)};
            },
        ).filter((sample) => Number.isFinite(sample.value));
        if (
            samples.length === 0
            || samples.every((sample) => Math.abs(sample.value) <= SketchTrimCornerGeometryService.ROOT_TOLERANCE)
        ) {
            // 全域近零表示两条支撑曲线重合，交点不唯一，Corner 必须失败。
            return [];
        }

        const roots: number[] = [];
        for (let index = 0; index < samples.length; index += 1) {
            const sample = samples[index];
            if (Math.abs(sample.value) <= SketchTrimCornerGeometryService.ROOT_TOLERANCE) {
                roots.push(sample.parameterT);
            }
            const next = samples[index + 1];
            if (next && sample.value * next.value < 0) {
                roots.push(this.refineBracketRoot(evaluator, sample.parameterT, next.parameterT));
            }
            const previous = samples[index - 1];
            if (
                previous
                && next
                && Math.abs(sample.value) <= Math.abs(previous.value)
                && Math.abs(sample.value) <= Math.abs(next.value)
            ) {
                const minimumRoot = this.refineMinimumRoot(
                    evaluator,
                    previous.parameterT,
                    next.parameterT,
                );
                if (minimumRoot !== null) {
                    roots.push(minimumRoot);
                }
            }
        }
        return this.dedupeNumbers(roots);
    }

    /**
     * 二分收敛异号根。
     *
     * @param evaluator 标量函数。
     * @param startT 左端点。
     * @param endT 右端点。
     * @returns 收敛参数。
     */
    private refineBracketRoot(
        evaluator: (parameterT: number) => number,
        startT: number,
        endT: number,
    ): number {
        let leftT = startT;
        let rightT = endT;
        let leftValue = evaluator(leftT);
        for (let iteration = 0; iteration < 64; iteration += 1) {
            const middleT = (leftT + rightT) / 2;
            const middleValue = evaluator(middleT);
            if (
                Math.abs(middleValue) <= SketchTrimCornerGeometryService.ROOT_TOLERANCE
                || rightT - leftT <= SketchTrimCornerGeometryService.PARAMETER_TOLERANCE
            ) {
                return middleT;
            }
            if (leftValue * middleValue <= 0) {
                rightT = middleT;
            } else {
                leftT = middleT;
                leftValue = middleValue;
            }
        }
        return (leftT + rightT) / 2;
    }

    /**
     * 收敛同号区间中的近零极小值，用于识别相切根。
     *
     * @param evaluator 标量函数。
     * @param startT 区间起点。
     * @param endT 区间终点。
     * @returns 达到根容差时返回参数，否则返回 null。
     */
    private refineMinimumRoot(
        evaluator: (parameterT: number) => number,
        startT: number,
        endT: number,
    ): number | null {
        let leftT = startT;
        let rightT = endT;
        for (let iteration = 0; iteration < 64; iteration += 1) {
            const leftThirdT = leftT + (rightT - leftT) / 3;
            const rightThirdT = rightT - (rightT - leftT) / 3;
            if (Math.abs(evaluator(leftThirdT)) <= Math.abs(evaluator(rightThirdT))) {
                rightT = rightThirdT;
            } else {
                leftT = leftThirdT;
            }
        }
        const parameterT = (leftT + rightT) / 2;
        return Math.abs(evaluator(parameterT)) <= SketchTrimCornerGeometryService.ROOT_TOLERANCE
            ? parameterT
            : null;
    }

    /**
     * 由连续整数采样值插值多项式系数。
     *
     * @param values `x=0..n` 对应的函数值。
     * @returns 按常数项到最高次项排列的系数。
     */
    private interpolatePolynomial(values: number[]): number[] {
        const size = values.length;
        const matrix = values.map((value, rowIndex) => {
            const row = Array.from({length: size}, (_, columnIndex) => rowIndex ** columnIndex);
            row.push(value);
            return row;
        });

        // 使用带主元选择的高斯消元，避免四次插值在常见尺度下放大舍入误差。
        for (let pivotIndex = 0; pivotIndex < size; pivotIndex += 1) {
            let bestRowIndex = pivotIndex;
            for (let rowIndex = pivotIndex + 1; rowIndex < size; rowIndex += 1) {
                if (Math.abs(matrix[rowIndex][pivotIndex]) > Math.abs(matrix[bestRowIndex][pivotIndex])) {
                    bestRowIndex = rowIndex;
                }
            }
            [matrix[pivotIndex], matrix[bestRowIndex]] = [matrix[bestRowIndex], matrix[pivotIndex]];
            const pivot = matrix[pivotIndex][pivotIndex];
            if (Math.abs(pivot) <= Number.EPSILON) {
                continue;
            }
            for (let columnIndex = pivotIndex; columnIndex <= size; columnIndex += 1) {
                matrix[pivotIndex][columnIndex] /= pivot;
            }
            for (let rowIndex = 0; rowIndex < size; rowIndex += 1) {
                if (rowIndex === pivotIndex) {
                    continue;
                }
                const factor = matrix[rowIndex][pivotIndex];
                for (let columnIndex = pivotIndex; columnIndex <= size; columnIndex += 1) {
                    matrix[rowIndex][columnIndex] -= factor * matrix[pivotIndex][columnIndex];
                }
            }
        }
        return matrix.map((row) => row[size]);
    }

    /**
     * 递归求解低阶实系数多项式的全部实根。
     *
     * @param sourceCoefficients 常数项到最高次项系数。
     * @returns 去重实根。
     */
    private solveRealPolynomialRoots(sourceCoefficients: number[]): number[] {
        const coefficients = [...sourceCoefficients];
        while (
            coefficients.length > 1
            && Math.abs(coefficients[coefficients.length - 1]) <= 1e-10
        ) {
            coefficients.pop();
        }
        const degree = coefficients.length - 1;
        if (degree <= 0) {
            return [];
        }
        if (degree === 1) {
            return [-coefficients[0] / coefficients[1]];
        }

        const derivative = coefficients.slice(1).map((coefficient, index) => coefficient * (index + 1));
        const criticalPoints = this.solveRealPolynomialRoots(derivative);
        const leading = Math.abs(coefficients[degree]);
        const bound = 1 + Math.max(...coefficients.slice(0, -1).map((value) => Math.abs(value) / leading));
        const intervalPoints = [-bound, ...criticalPoints.filter((value) => Math.abs(value) <= bound), bound]
            .sort((left, right) => left - right);
        const roots: number[] = [];

        intervalPoints.forEach((parameterT) => {
            if (Math.abs(this.evaluatePolynomial(coefficients, parameterT)) <= 1e-6) {
                roots.push(parameterT);
            }
        });
        for (let index = 0; index < intervalPoints.length - 1; index += 1) {
            const leftT = intervalPoints[index];
            const rightT = intervalPoints[index + 1];
            const leftValue = this.evaluatePolynomial(coefficients, leftT);
            const rightValue = this.evaluatePolynomial(coefficients, rightT);
            if (leftValue * rightValue < 0) {
                roots.push(this.refineBracketRoot(
                    (parameterT) => this.evaluatePolynomial(coefficients, parameterT),
                    leftT,
                    rightT,
                ));
            }
        }
        return this.dedupeNumbers(roots);
    }

    /**
     * 使用 Horner 法计算多项式值。
     *
     * @param coefficients 常数项到最高次项系数。
     * @param parameterT 参数。
     * @returns 多项式值。
     */
    private evaluatePolynomial(coefficients: number[], parameterT: number): number {
        let value = 0;
        for (let index = coefficients.length - 1; index >= 0; index -= 1) {
            value = value * parameterT + coefficients[index];
        }
        return value;
    }

    /**
     * 解析参考面上的圆或椭圆点。
     *
     * @param referencePlane 参考面。
     * @param centerWorld 中心世界坐标。
     * @param radiusX X 半径。
     * @param radiusY Y 半径。
     * @param rotation 椭圆旋转角。
     * @param angle 参数角。
     * @returns 世界坐标。
     */
    private resolvePlanarConicPoint(
        referencePlane: SketchTrimCurveObject['referencePlane'],
        centerWorld: THREE.Vector3,
        radiusX: number,
        radiusY: number,
        rotation: number,
        angle: number,
    ): THREE.Vector3 {
        const localCenter = referencePlane.worldToLocal(centerWorld);
        const localPoint = new THREE.Vector2(
            localCenter.x + radiusX * Math.cos(angle),
            localCenter.y + radiusY * Math.sin(angle),
        ).rotateAround(localCenter, rotation);
        return referencePlane.localToWorld(localPoint);
    }

    /**
     * 把世界点转换到椭圆未旋转的规范坐标。
     *
     * @param ellipse 椭圆对象。
     * @param point 世界点。
     * @returns 以椭圆中心为原点的规范二维坐标。
     */
    private resolveEllipseCanonicalPoint(ellipse: SketchEllipseObject, point: THREE.Vector3): THREE.Vector2 {
        const definition = ellipse.getEllipseDefinition();
        const localCenter = ellipse.referencePlane.worldToLocal(definition.centerWorld);
        const localPoint = ellipse.referencePlane.worldToLocal(point);
        return new THREE.Vector2(localPoint.x, localPoint.y)
            .rotateAround(localCenter, -definition.rotation)
            .sub(localCenter);
    }

    /**
     * 解析抛物线局部框架。
     *
     * @param parabola 抛物线对象。
     * @returns 顶点、轴向、侧向及焦距；定义退化时返回 null。
     */
    private resolveParabolaFrame(parabola: SketchParabolaObject): {
        apexWorld: THREE.Vector3;
        axisDirection: THREE.Vector3;
        sideDirection: THREE.Vector3;
        focalLength: number;
    } | null {
        const definition = parabola.getParabolaDefinition();
        const normal = parabola.referencePlane.normal.clone().normalize();
        const axisDirection = definition.focalWorld.clone().sub(definition.apexWorld);
        axisDirection.sub(normal.clone().multiplyScalar(axisDirection.dot(normal)));
        const focalLength = axisDirection.length();
        if (focalLength <= SketchTrimCornerGeometryService.POINT_TOLERANCE) {
            console.error(`[SketchTrimCornerGeometryService] Corner 抛物线定义无效：焦点与顶点重合，实体=${parabola.uuid}。`);
            return null;
        }
        axisDirection.normalize();
        return {
            apexWorld: definition.apexWorld.clone(),
            axisDirection,
            sideDirection: new THREE.Vector3().crossVectors(axisDirection, normal).normalize(),
            focalLength,
        };
    }

    /**
     * 校验曲线自然支撑几何是否可参与 Corner 求交。
     *
     * @param curveData 待校验曲线数据。
     * @returns 支撑几何有效时返回 true。
     */
    private hasValidSupportGeometry(curveData: SketchTrimCurveData): boolean {
        if (curveData.curveKind !== SketchTrimCurveKind.Parabola) {
            return true;
        }
        return this.resolveParabolaFrame(curveData.curve as SketchParabolaObject) !== null;
    }

    /**
     * 解析有向角度扫掠量。
     *
     * @param startAngle 起始角。
     * @param endAngle 终止角。
     * @param clockwise 是否顺时针。
     * @returns `[0, 2π]` 内扫掠量。
     */
    private resolveAngleSweep(startAngle: number, endAngle: number, clockwise: boolean): number {
        const rawSweep = clockwise ? startAngle - endAngle : endAngle - startAngle;
        const normalizedSweep = this.normalizePositiveAngle(rawSweep);
        if (
            normalizedSweep <= SketchTrimCornerGeometryService.PARAMETER_TOLERANCE
            && Math.abs(rawSweep) > SketchTrimCornerGeometryService.PARAMETER_TOLERANCE
        ) {
            return SketchTrimCornerGeometryService.FULL_SWEEP;
        }
        return normalizedSweep;
    }

    /**
     * 将角度规范到 `[0, 2π)`。
     *
     * @param angle 原始角度。
     * @returns 正角度。
     */
    private normalizePositiveAngle(angle: number): number {
        const normalized = angle % SketchTrimCornerGeometryService.FULL_SWEEP;
        return normalized < 0 ? normalized + SketchTrimCornerGeometryService.FULL_SWEEP : normalized;
    }

    /**
     * 对有限数值排序去重。
     *
     * @param values 原始数值。
     * @returns 去重数值。
     */
    private dedupeNumbers(values: number[]): number[] {
        return values
            .filter((value) => Number.isFinite(value))
            .sort((left, right) => left - right)
            .reduce<number[]>((result, value) => {
                const previous = result[result.length - 1];
                if (
                    previous === undefined
                    || Math.abs(previous - value) > SketchTrimCornerGeometryService.PARAMETER_TOLERANCE
                ) {
                    result.push(value);
                }
                return result;
            }, []);
    }
}
